import { useCallback, useEffect, useMemo, useState } from 'react';
import '../backlog/backlog.css';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar } from '../../components/AppToolbar';
import { useGridTextSearch } from './useGridTextSearch';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import { useNavActions } from '../../hooks/useNavActions';
import { guardedExit, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { ReagentListShell } from './ReagentListShell';
import { GridSearch } from '../../components/GridSearch';
import { useAuthStore } from '../../auth/store';
import { toWire } from '../../utils/datetime';
import { DateRangeSelect } from '../modules/DateRangeSelect';
import { depleteLlts, fetchLltDepletion } from './api';
import { REAGENT_RANGES, rangeCode, rangeDays, type LltDepletionRow, type ReagentDateFilter } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Reagent Preparation → LLT Depletion (nav `rgp-lltdepletion`).
// Port of legacy ALPACA `ReagentPreparation_ListView_LLTDepletion`; spec §16.2.
//
// The list shows only preparations that are NOT yet depleted and are NOT
// Calibration (a fixed server-side criterion, ctrl :488).  Tick the lots that
// are used up, then Save.
//
// TWO DELIBERATE DIFFERENCES FROM LEGACY, both narrowing the failure surface:
//
//  1. Legacy writes DateDepleted / DepletedBy into the row the instant it is
//     ticked (Grid_SelectionChanged), clears them on untick, and only re-checks
//     them at Save — hence spec messages 13 and 14. The grid now SHOWS that the
//     same way (owner request 2026-10-16 — without it this screen looked like
//     LLT Search): a ticked row reads "now" and the signed-in user, an untick
//     clears it. Those are DISPLAY only: the SERVER still stamps both values,
//     inside one transaction, from its own clock and the token's user, so the
//     two messages still cannot occur and nothing typed in the browser is trusted.
//  2. The save is idempotent server-side, so a double-click or a retry cannot
//     re-stamp a lot with a later date.  If every ticked row was already
//     depleted by someone else, the user is told exactly that instead of
//     seeing a silent "Saved successfully.".
//
// Message 12 ("Please select atleast one record to proceed further!") is kept
// verbatim and enforced on BOTH sides — here for UX, in the controller for real
// (§14b.5: the server is authoritative).
// ─────────────────────────────────────────────────────────────────────────────

const COLUMNS: AppColumn<LltDepletionRow>[] = [
  { field: 'llt',            header: 'LLT',            width: 120 },
  // Legacy ColumnInfo Caption — the column shows PrepName under the heading
  // "StandardName". Kept, because that is the wording analysts look for.
  { field: 'prepName',       header: 'StandardName',   width: 220 },
  { field: 'finalVolume',    header: 'FinalVolume',    width: 110 },
  { field: 'prepType',       header: 'PrepType',       width: 110 },
  { field: 'department',     header: 'Department',     width: 120 },
  // Date only, as the live G5 grid shows it (owner request 2026-11-03).
  { field: 'dateDepleted',   header: 'DateDepleted',   width: 120, type: 'date' },
  { field: 'depletedBy',     header: 'DepletedBy',     width: 130 },
  { field: 'expirationDate', header: 'ExpirationDate', width: 120, type: 'date' },
  { field: 'datePrepared',   header: 'DatePrepared',   width: 120, type: 'date' },   // date only, as the live G5 grid
  { field: 'preparedBy',     header: 'PreparedBy',     width: 130 },
];

export default function LltDepletionPage({ onHome }: { onHome?: () => void }) {
  const notify   = useNotify();
  const runSave  = useSaveFlow();
  // MAC: Save needs Write on ReagentPreparation_ListView_LLTDepletion — hidden otherwise (the API refuses it too).
  const perm     = useNavActions('rgp-lltdepletion');
  const [rows, setRows]       = useState<LltDepletionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  // When each ticked row was ticked — shown as its DateDepleted until Save.
  const [tickedAt, setTickedAt] = useState<Record<string, string>>({});
  const meName = useAuthStore((s) => s.user?.name || s.user?.email || '');

  /** XAF Grid_SelectionChanged: tick → DateDepleted = now, DepletedBy = me; untick → cleared. */
  function onSelect(next: Set<string | number>) {
    setSelected(next);
    setTickedAt((cur) => {
      const out: Record<string, string> = {};
      const now = toWire(new Date());
      for (const id of next) out[String(id)] = cur[String(id)] ?? now;
      return out;
    });
  }

  const shownRows = useMemo(
    () => rows.map((r) => (selected.has(r.oid)
      ? { ...r, dateDepleted: tickedAt[r.oid] ?? r.dateDepleted, depletedBy: meName || r.depletedBy }
      : r)),
    [rows, selected, tickedAt, meName]);
  const [dateFilter, setDateFilter] = useState<ReagentDateFilter | null>(null);
  // Two boxes, as G5: the toolbar Search and the grid's own find panel (XAF
  // ShowFindPanel="True"). A row must match both.
  const [search, setSearch] = useState('');
  const [find, setFind] = useState('');
  const visibleRows = useGridTextSearch(useGridTextSearch(shownRows, COLUMNS, search, 'contains'), COLUMNS, find, 'contains');

  // A search that HIDES a ticked row also unticks it. Depletion cannot be undone,
  // and a tick the user can no longer see used to be depleted by Save all the same —
  // the only hint was the count in the confirm ("Mark 2 lab lot(s)…" with one row on
  // screen; user report 2026-11-04). So what is ticked is always what is visible.
  const visibleKey = visibleRows.map((r) => r.oid).join('|');
  useEffect(() => {
    if (selected.size === 0) return;
    const shown = new Set(visibleRows.map((r) => r.oid));
    const kept = [...selected].filter((id) => shown.has(String(id)));
    if (kept.length !== selected.size) onSelect(new Set(kept));
    // visibleKey stands for visibleRows; selected is read, not watched — a new tick
    // is always on a visible row.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey]);

  // Ticked rows ARE pending changes — each already shows its Depleted Date / By —
  // and the shared tracker cannot see grid ticks, so register them (standing rule 15):
  // leaving with rows ticked asks "Unsaved changes will be lost" first.
  useUnsavedGuard(selected.size > 0);

  const load = useCallback(async (filter?: ReagentDateFilter) => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await fetchLltDepletion(filter);
      setRows(res.rows);
      setDateFilter(res.dateFilter);
      // Depleted rows drop out of the list, so any surviving selection would
      // point at rows that are no longer here.
      setSelected(new Set());
      setTickedAt({});
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Failed to load LLT depletion.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  async function save() {
    if (selected.size === 0) {
      // Spec §18 message 12, verbatim (including the original spelling).
      notify.warning('Please select atleast one record to proceed further!');
      return;
    }
    const ids = [...selected].map(String);

    // The shared flow gives the ALPACA confirm → blocking loader →
    // "Saved successfully." → reason capture, all for free (§18.6).
    // `successMessage` is a FUNCTION because the wording depends on what the
    // save returned; a plain string would be fixed before `save` ever ran.
    let depleted = 0;
    await runSave({
      formName: 'LLT Depletion',
      auditAction: 'edit',
      confirmMessage: `Mark ${ids.length} lab lot(s) as depleted?`,
      save: async () => { depleted = (await depleteLlts(ids)).depleted; },
      successMessage: () =>
        depleted === 0
          ? 'Those lab lots were already depleted. Nothing changed.'
          : depleted === ids.length
            ? 'Saved successfully.'
            : `Saved successfully. ${depleted} of ${ids.length} marked depleted — the rest were already depleted.`,
      onSaved: () => { void load(dateFilter ?? undefined); },
    });
  }

  // Save through the ONE shared hotkey layer (standing rule 14) — Ctrl+S fires even mid-search.
  useHotkeys(perm.write ? [{
    combo: 'mod+s', label: 'Save', group: 'LLT Depletion', order: 1,
    // Enabled with nothing ticked, as G5: Save then shows message 12 instead of
    // being greyed out (owner request 2026-11-04).
    disabled: loading, run: () => void save(),
  }] : []);

  // No List / Detail switch: LLT Depletion has no detail view (legacy is a list you tick and Save).
  return (
    <ReagentListShell
      pageId="rgp-lltdepletion" caption="LLT Depletion" auditFormName="LLT Depletion"
      loadMs={loadMs} loading={loading} onHome={onHome}
      // The live G5 bar (owner screenshot 2026-11-03): Refresh · Save · Date Filter ·
      // Export to · Search — `refreshFirst` puts Refresh ahead of the `left` slot.
      // A reload drops the ticks, so Refresh and a Date Filter change ask first.
      toolbar={<AppToolbar
        refreshFirst
        left={perm.write && (
          <button type="button" className="ca-btn" onClick={() => void save()}
                  disabled={loading} title={`Save (${formatCombo('mod+s')})`}>
            <span className="tb-i">💾</span> Save
          </button>
        )}
        onRefresh={() => void guardedExit(() => void load(dateFilter ?? undefined))}
        refreshing={loading}
        beforeExport={
          <DateRangeSelect
            variant="field" label="Date Filter" ranges={REAGENT_RANGES}
            value={dateFilter ? rangeDays(dateFilter) : null}
            onChange={(days) => void guardedExit(() => { const c = rangeCode(days); setDateFilter(c); void load(c); })}
          />
        }
        // G5's split Export: the button exports CSV, its ▾ opens every format.
        exportData={{ filename: 'llt-depletion', ...gridExportMatrix(visibleRows, COLUMNS), defaultCsv: true }}
        right={<GridSearch variant="toolbar" applyOnEnter value={search} onChange={setSearch} placeholder="Text to search..." title="Filter records by text" />}
      />}
    >
      <AppDataGrid<LltDepletionRow>
                  selectAllTitle="Selection state of all rows on all pages"
        rows={visibleRows}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        selectable
        selectedIds={selected}
        onSelectionChange={onSelect}
        // G5 (DevExpress select-by-row-click): clicking a row SELECTS it — that row
        // alone — and fills its Depleted Date / By, exactly as ticking it does; the
        // tick boxes still add rows one at a time (owner request 2026-11-03).
        onRowClick={(r) => onSelect(new Set([r.oid]))}
        headerLeft={<GridSearch value={find} onChange={setFind} placeholder="Enter text to search..." style={{ padding: 0 }} />}
        highlightText={find}
        emptyText={search || find ? 'No results match your search.' : 'No lab lots awaiting depletion for this date range.'}
      />
    </ReagentListShell>
  );
}
